import type { CSSProperties, ReactNode } from 'react';
import type { ButtonProps, ModalProps } from 'react-bootstrap';
import { Button } from 'react-bootstrap';
import BaseModal from './BaseModal';

/**
 * Displays a confirmation dialog with cancel and confirm actions.
 *
 * @param props The confirmation modal props.
 * @returns The rendered confirmation modal.
 */
const ConfirmationModal = ({
  show = false,
  handleShow = () => {},
  bodyTxt,
  cancelBtnTxt,
  confirmBtnTxt,
  confirmHandler = () => {},
  cancelHandler = handleShow,
  confirmButtonStyle = {},
  cancelButtonStyle = {},
  confirmButtonClassName = '',
  confirmButtonVariant = 'primary',
  customBodyTxtClassName = 'fs-5 max-w-3xs mb-3',
  ...props
}: Omit<ModalProps, 'show' | 'onHide' | 'size' | 'children'> & {
  show?: boolean;
  handleShow?: (show?: boolean) => void;
  bodyTxt?: ReactNode;
  cancelBtnTxt?: string;
  confirmBtnTxt?: string;
  confirmHandler?: () => void | Promise<void>;
  cancelHandler?: (show?: boolean) => void | Promise<void>;
  confirmButtonStyle?: CSSProperties;
  cancelButtonStyle?: CSSProperties;
  confirmButtonClassName?: string;
  confirmButtonVariant?: ButtonProps['variant'];
  customBodyTxtClassName?: string;
  children?: ReactNode;
  size?: NonNullable<ModalProps['size']> | 'md';
}): JSX.Element => {
  const handleModalHide: ModalProps['onHide'] = () => {
    handleShow?.(false);
  };

  return (
    <BaseModal
      show={show}
      size="md"
      handleShow={handleModalHide}
      closeButton={false}
      backgroundColor="#fff"
      borderWidth="2px"
      borderColor="#436C9A"
      {...props}
    >
      <div className="d-flex flex-column justify-content-center align-items-center text-black text-center">
        <div className={customBodyTxtClassName}>{bodyTxt}</div>
      </div>
      <div className="d-flex flex-column w-100 gap-2 flex-sm-row justify-content-sm-around">
        {cancelBtnTxt && (
          <Button
            className="rounded-4 bg-dark fs-6 w-sm-50 btn-cancel"
            onClick={(e) => {
              e.stopPropagation();
              void cancelHandler();
            }}
            variant="secondary"
            style={cancelButtonStyle}
          >
            {cancelBtnTxt}
          </Button>
        )}
        <Button
          className={`rounded-4 fs-6 w-sm-50 ${confirmButtonClassName}`}
          onClick={(e) => {
            e.stopPropagation();
            void confirmHandler();
          }}
          variant={confirmButtonVariant}
          style={confirmButtonStyle}
        >
          {confirmBtnTxt}
        </Button>
      </div>
    </BaseModal>
  );
};

export default ConfirmationModal;
